<script>
    import { createEventDispatcher, getContext, onMount } from "svelte";

    import { fetchMediaItems, getArtworkURL } from "../utils.js";

    import { TOASTS_KEY } from "./toasts/toast.js";

    export let item;
    export let top;
    export let left;
    export let target;

    const { addToast, addPromiseToast } = getContext(TOASTS_KEY);
    
    function cumulativeOffset(element) {
        let top = 0;
        let left = 0;

        do {
            top += element.offsetTop || 0;
            left += element.offsetLeft || 0;
            element = element.offsetParent;
        } while (element);

        return { top, left };
    }

    let root;
    onMount(() => {
        // console.log("pageX:", left);
        // console.log("pageY:", top);
        
        const rect = root.getBoundingClientRect();
        if (rect.top < 0) {
            top += rect.top;
        }
        if (rect.bottom > document.body.clientHeight) {
            top -= (rect.bottom - document.body.clientHeight);
        }
        if (rect.left < 0) {
            left += rect.left;
        }
        if (rect.right > document.body.clientWidth) {
            left -= (rect.right - document.body.clientWidth);
        }
        
        let savedStyles;
        if (target) {
            const targetRect = target.getBoundingClientRect();
            const targetOffset = cumulativeOffset(target.offsetParent);
            
            const computedStyle = getComputedStyle(target);

            const targetTop = targetRect.top - targetOffset.top;
            const targetLeft = targetRect.left - targetOffset.left;

            // console.log("computedStyle.top:", computedStyle.top);
            // console.log("computedStyle.bottom:", computedStyle.bottom);
            // console.log("computedStyle.width:", computedStyle.width);
            // console.log("computedStyle.height:", computedStyle.height);
            
            const targetWidth = computedStyle.width;
            const targetHeight = computedStyle.height;            

            // const targetWidth = targetRect.width;
            // const targetHeight = targetRect.height;

            savedStyles = { ...target.style };

            target.style.position = "absolute";
            target.style.top = `${targetTop}px`;
            target.style.left = `${targetLeft}px`;
            target.style.width = `${targetWidth}`;
            target.style.height = `${targetHeight}`;
            target.style.zIndex = "10001";

            target.classList.add("context-menu-active");
        }

        return function () {
            if (target) {
                target.style = savedStyles;
                target.classList.remove("context-menu-active");
            }
        };
    });

    const music = MusicKit.getInstance();

    const dispatch = createEventDispatcher();

    // $: console.log("item:", item);
    $: attrs = item.attributes;
    $: explicit = (attrs.contentRating == "explicit");
    $: artworkUrl = attrs && getArtworkURL(item, 40, 40) || '/missing-artwork.svg';
    $: isLibrary = attrs?.playParams?.isLibrary ?? false;

    function handlePlayNow() {
        dispatch("dismiss");
        const promise = (async () => {
            const items = await fetchMediaItems(item.type, item.id);
            await music.setQueue({ items });
            await music.changeToMediaAtIndex(0);
        })();
        addPromiseToast(promise, {
            pending: {
                kind: "info",
                content: "Scheduling the item to play now ...",
            },
            success: {
                content: "The item is now playing !",
                duration: 5000,
            },
            error: {
                content: "An error happened while attempting to schedule the item.",
                duration: 5000,
            },
        });
    }

    function handlePlayNext() {
        dispatch("dismiss");
        const promise = (async () => {
            const items = await fetchMediaItems(item.type, item.id);
            await music.playNext({ items });
        })();
        addPromiseToast(promise, {
            pending: {
                kind: "info",
                content: "Scheduling the item to play next ...",
            },
            success: {
                content: "The item will play right after the current song !",
                duration: 5000,
            },
            error: {
                content: "An error happened while attempting to schedule the item.",
                duration: 5000,
            },
        });
    }

    function handlePlayLater() {
        dispatch("dismiss");
        const promise = (async () => {
            const items = await fetchMediaItems(item.type, item.id);
            await music.playLater({ items });
        })();
        addPromiseToast(promise, {
            pending: {
                kind: "info",
                content: "Scheduling the item to play later ...",
            },
            success: {
                content: "The item will play after the current queue !",
                duration: 5000,
            },
            error: {
                content: "An error happened while attempting to schedule the item.",
                duration: 5000,
            },
        });
    }

    function handleAddToLibrary() {
        dispatch("dismiss");
        const promise = (async () => {
            await music.api.music(
                `/v1/me/library`,
                { [`ids[${item.type}]`]: item.id },
                { fetchOptions: { method: "POST" } },
            );
        })();
        addPromiseToast(promise, {
            pending: {
                kind: "info",
                content: "Hold tight, we are adding the item to your library ...",
            },
            success: {
                content: "The item was successfully added to your library !",
                duration: 5000,
            },
            error: {
                content: "An error happened while adding the item to your library.",
                duration: 5000,
            },
        });
    }
</script>

<div class="overlay" on:click={() => dispatch("dismiss")}></div>
<div class="root" style="top: {top}px; left: {left}px" bind:this={root}>
    <div class="grid">
        <div class="item" on:click={() => { dispatch("open-modal", item); dispatch("dismiss") }}>
            <img class="art"
                 src="{artworkUrl}"
                 alt="{attrs.artistName}"
                 draggable="false"
                 style="--size: 40px">
            <div class="desc">
                <div class="name-line" style="--template-columns: {explicit ? "auto min-content 1fr" : "auto 1fr"}">
                    <div class="name" title="{attrs.name}">{attrs.name}</div>
                    {#if explicit}
                        <div class="explicit" title="Explicit song">E</div>
                    {/if}
                </div>
                {#if attrs.artistName}
                    <div class="artist-album" title="{attrs.artistName}">
                        {attrs.artistName}
                    </div>
                {:else}
                    <div class="artist-album">
                        Personal playlist
                    </div>
                {/if}
            </div>
        </div>
        <div class="separator"></div>
        <div class="button" on:click={handlePlayNow}>
            Play Now
        </div>
        <div class="button" on:click={handlePlayNext}>
            Play Next
        </div>
        <div class="button" on:click={handlePlayLater}>
            Play Later
        </div>
        {#if !isLibrary}
            <div class="button" on:click={handleAddToLibrary}>
                Add To Library
            </div>
        {/if}
    </div>
</div>

<style>
    .overlay {
        position: absolute;
        top: 0;
        left: 0;
        width: 100%;
        height: 100%;
        background-color: rgba(0, 0, 0, 0.75);
        backdrop-filter: blur(8px);
        -webkit-backdrop-filter: blur(8px);
        z-index: 10000;
    }

    .root {
        position: absolute;
        width: 250px;
        display: grid;
        color: var(--app-bg-color);
        background-color: var(--app-fg-color);
        border-radius: 5px;
        border: solid 1px var(--app-bg-color);
        overflow: hidden;
        z-index: 10002;
    }

    .grid {
        width: 100%;
        height: 100%;
        display: flex;
        flex-direction: column;
    }

    .item {
        display: grid;
        grid-gap: 10px;
        grid-template-rows: 1fr;
        grid-template-columns: min-content 1fr min-content;
        grid-template-areas: "item-art desc";
        padding: var(--song-item-padding, 10px);
        cursor: pointer;
    }

    .art {
        grid-area: item-art;
        width: var(--size);
        height: var(--size);
        border-radius: 5px;
    }

    .desc {
        grid-area: desc;
        overflow: hidden;
        text-overflow: ellipsis;
        white-space: nowrap;
    }

    .name-line,
    .artist-album {
        display: inline;
    }

    .name-line {
        grid-area: song-name;
        display: grid;
        grid-template-rows: 1fr;
        grid-template-columns: var(--template-columns);
        grid-gap: 5px;
    }

    .name,
    .artist-album {
        text-overflow: ellipsis;
        white-space: nowrap;
        overflow: hidden;
    }

    .name {
        font-weight: bold;
    }

    .artist-album {
        font-size: 13px;
    }

    .explicit {
        font-size: 15px;
        font-weight: bold;
        color: var(--context-menu-explicit-fg-color, var(--app-explicit-fg-color));
        background-color: var(--context-menu-explicit-bg-color, var(--app-explicit-bg-color));
        border-radius: 2px;
        overflow: hidden;
        white-space: nowrap;
        text-overflow: ellipsis;
        text-align: left;
        padding: 0 5px;
    }

    .separator {
        background-color: var(--app-bg-color);
        height: 2px;
    }

    .button {
        width: 100%;
        padding: 10px;
        font-weight: bold;
        cursor: pointer;
        box-sizing: border-box;
        border-top: solid 1px;
        border-color: var(--app-bg-color);
    }

    .button:hover {
        color: var(--app-fg-color);
        background-color: var(--app-bg-color);
    }
</style>
